import { FlashList } from '@shopify/flash-list';
import { format, parseISO } from 'date-fns';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ScrollView, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import type {
  ChangelogCategory,
  ChangelogItem,
} from '@/api/partners/changelog/types';
import { useChangelogs } from '@/api/partners/changelog/use-changelogs';
import { useMarkChangelogsViewed } from '@/api/partners/changelog/use-mark-changelogs-viewed';
import type { TxKeyPath } from '@/core';
import mixpanelService from '@/core/clients/mixpanel';
import { translate } from '@/core/i18n';
import { Button, Card, Text } from '@/ui';
import { Chip, ChipType } from '@/ui/chip';
import { Loader } from '@/ui/loader';

type Filter = 'all' | ChangelogCategory;

type WhatsNewRow =
  | {
      type: 'header';
      id: string;
      publishedAt: string;
      appVersion: string;
    }
  | {
      type: 'card';
      id: string;
      item: ChangelogItem;
    };

const FILTERS: { id: Filter; tx: TxKeyPath }[] = [
  { id: 'all', tx: 'partner.whatsNew.all' },
  { id: 'new_feature', tx: 'partner.whatsNew.newFeature' },
  { id: 'improved', tx: 'partner.whatsNew.improved' },
  { id: 'fixed', tx: 'partner.whatsNew.fixed' },
];

const CHIP_TYPE: Record<ChangelogCategory, ChipType> = {
  new_feature: ChipType.BRAND,
  improved: ChipType.DEFAULT,
  fixed: ChipType.SUCCESS,
};

const CHIP_TX: Record<ChangelogCategory, TxKeyPath> = {
  new_feature: 'partner.whatsNew.newFeature',
  improved: 'partner.whatsNew.improved',
  fixed: 'partner.whatsNew.fixed',
};

export function WhatsNew() {
  const { styles } = useStyles(stylesheet);
  const [filter, setFilter] = useState<Filter>('all');
  const didMarkViewed = useRef(false);
  const { mutateAsync: markViewed } = useMarkChangelogsViewed();

  const { data, isPending, isFetchingNextPage, fetchNextPage, hasNextPage } =
    useChangelogs(filter === 'all' ? undefined : filter);

  useEffect(() => {
    mixpanelService.track('Page Viewed', {
      'Page Name': "Partner What's New",
    });
  }, []);

  const rows = useMemo(() => {
    const nextRows: WhatsNewRow[] = [];
    let lastHeaderKey: string | null = null;

    data?.pages.forEach((page) => {
      page.data.forEach((release) => {
        const headerKey = `${release.appVersion}-${release.publishedAt}`;

        if (headerKey !== lastHeaderKey) {
          nextRows.push({
            type: 'header',
            id: `header-${headerKey}`,
            publishedAt: release.publishedAt,
            appVersion: release.appVersion,
          });
          lastHeaderKey = headerKey;
        }

        release.items.forEach((item) => {
          nextRows.push({
            type: 'card',
            id: `card-${item.id}`,
            item,
          });
        });
      });
    });

    return nextRows;
  }, [data]);

  useEffect(() => {
    if (didMarkViewed.current || isPending || rows.length === 0) {
      return;
    }

    didMarkViewed.current = true;
    markViewed().catch(() => {
      didMarkViewed.current = false;
    });
  }, [isPending, rows.length, markViewed]);

  const renderItem = ({
    item,
    index,
  }: {
    item: WhatsNewRow;
    index: number;
  }) => {
    if (item.type === 'header') {
      return (
        <View
          style={[styles.headerRow, index === 0 ? null : styles.headerSpacing]}
        >
          <Text type="bodyRegular" style={styles.headerText}>
            {translate('partner.whatsNew.versionDate', {
              version: item.appVersion,
              date: format(parseISO(item.publishedAt), 'MMMM d, yyyy'),
            })}
          </Text>
          <View style={styles.hairline} />
        </View>
      );
    }

    const { category, title, body, bulletPoints } = item.item;

    return (
      <Card style={styles.card}>
        <Chip type={CHIP_TYPE[category]} text={translate(CHIP_TX[category])} />

        <View style={styles.cardCopy}>
          <Text type="title2">{title}</Text>

          {body ? (
            <Text type="bodyRegular" style={styles.secondaryText}>
              {body}
            </Text>
          ) : null}

          {bulletPoints?.map((point) => (
            <Text key={point} type="bodyRegular" style={styles.secondaryText}>
              {`\u2022  ${point}`}
            </Text>
          ))}
        </View>
      </Card>
    );
  };

  return (
    <View style={styles.container}>
      <View style={styles.content}>
        <View style={styles.nav}>
          <Text type="title1" tx="partner.whatsNew.title" />
        </View>

        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={styles.filterScroll}
          contentContainerStyle={styles.filters}
        >
          {FILTERS.map((filterItem) => (
            <Button
              key={filterItem.id}
              tx={filterItem.tx}
              type={
                filter === filterItem.id ? 'primarySmall' : 'secondarySmall'
              }
              onPress={() => {
                setFilter(filterItem.id);
              }}
              style={styles.filterButton}
            />
          ))}
        </ScrollView>

        {isPending ? (
          <View style={styles.loaderContainer}>
            <Loader style={{ width: 150, height: 150 }} />
          </View>
        ) : rows.length === 0 ? (
          <View style={styles.emptyContainer}>
            <Text
              type="bodyRegular"
              tx="partner.whatsNew.empty"
              style={styles.secondaryText}
            />
          </View>
        ) : (
          <FlashList
            data={rows}
            renderItem={renderItem}
            keyExtractor={(row) => row.id}
            getItemType={(row) => row.type}
            estimatedItemSize={160}
            style={styles.list}
            onEndReached={() => {
              if (hasNextPage && !isFetchingNextPage) {
                fetchNextPage();
              }
            }}
            onEndReachedThreshold={0.4}
            showsVerticalScrollIndicator={false}
            ListFooterComponent={
              isFetchingNextPage ? <Loader style={{ height: 140 }} /> : null
            }
          />
        )}
      </View>
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background_1,
  },
  content: {
    flex: 1,
    paddingLeft: {
      xs: 16,
      md: 56,
    },
    paddingRight: {
      xs: 16,
      md: 56,
    },
    paddingTop: {
      xs: 16,
      md: 56,
    },
    paddingBottom: 24,
    gap: 24,
  },
  nav: {
    display: {
      xs: 'none',
      md: 'flex',
    },
  },
  loaderContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.colors.background_1,
  },
  filterScroll: {
    flexGrow: 0,
    flexShrink: 0,
  },
  filters: {
    gap: 8,
    flexDirection: 'row',
    alignItems: 'center',
  },
  list: {
    flex: 1,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  filterButton: {
    width: 'auto',
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    width: '100%',
  },
  headerText: {
    flexShrink: 0,
  },
  headerSpacing: {
    marginTop: 32,
  },
  hairline: {
    flex: 1,
    height: 1,
    backgroundColor: theme.colors.card.default.borderColor,
  },
  card: {
    marginTop: 16,
    padding: 16,
    gap: 16,
    borderRadius: 16,
    width: '100%',
    maxWidth: {
      md: 779,
    },
    alignSelf: 'flex-start',
  },
  cardCopy: {
    gap: 8,
  },
  secondaryText: {
    color: theme.colors.text.secondary,
  },
}));
